/**
 * The slot rule `<schmancy-time-picker>` renders — arithmetic only, no DOM, so
 * the one thing about this primitive that must not be got wrong can be read,
 * reasoned about and executed on its own.
 */

/** The last minute-of-day a wall clock can read. */
export const DAY_END = 23 * 60 + 59

/**
 * `'HH:mm'` or `'HH:mm:ss'` split into minutes past local midnight and the
 * second within that minute, or `null` for anything that is neither — an empty
 * bound, a raw `datetime-local`, a typo. A bound that cannot be read is a bound
 * that is not applied, never one that empties a column.
 */
export function parseClock(value: string | null | undefined): { minutes: number; seconds: number } | null {
	const parsed = value ? /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value) : null
	if (!parsed) return null
	const minutes = Number(parsed[1]) * 60 + Number(parsed[2])
	const seconds = Number(parsed[3] ?? 0)
	return minutes >= 0 && minutes <= DAY_END && seconds >= 0 && seconds <= 59 ? { minutes, seconds } : null
}

/**
 * Every selectable minute-of-day, ascending.
 *
 * The slots are the multiples of `minuteStep` inside `[min, max]`, PLUS `min`
 * and `max` themselves whenever they do not fall on that grid. A window of
 * `[08:57, 08:57]` therefore offers exactly one slot, `08:57`, and the rule
 * leaves a column somewhere to rest for any `min ≤ max` — where a bare grid
 * would offer nothing, and a surface that can produce no legal value is a
 * surface whose Save is refused forever. At the default step of 1 every minute
 * is already on the grid; the endpoint rule is what protects the consumer who
 * coarsens it to 5 or 15.
 *
 * A bound is admitted off-grid only when it was DECLARED: the 00:00/23:59
 * fallbacks below are the day's own ends, not a window anyone asked for. Bounds
 * are read to the minute — a `:ss` tail on one narrows nothing, since seconds
 * are their own independent grid.
 */
export function timeSlots(minuteStep: number, min?: string, max?: string): number[] {
	const step = Math.max(1, Math.floor(minuteStep) || 1)
	const lowerBound = parseClock(min)?.minutes ?? null
	const upperBound = parseClock(max)?.minutes ?? null
	const lower = lowerBound ?? 0
	const upper = upperBound ?? DAY_END
	if (upper < lower) return []
	const slots = new Set<number>()
	if (lowerBound !== null) slots.add(lower)
	if (upperBound !== null) slots.add(upper)
	for (let m = Math.ceil(lower / step) * step; m <= upper; m += step) slots.add(m)
	return [...slots].sort((a, b) => a - b)
}
